import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import i18n from "../i18n";
import "./nav-groups.css";

const tr = (...a) => i18n.t(...a);

/**
 * Ομαδοποιημένο μενού πλοήγησης με αυτόματο overflow.
 *
 * Το πρόβλημα που λύνει: με 18 συνδέσμους το μενού «έσπαγε» πάνω στο λογότυπο και
 * στο κουμπί αποσύνδεσης σε οθόνες laptop. Εδώ οι σύνδεσμοι ομαδοποιούνται και,
 * όσοι ΔΕΝ χωρούν στο διαθέσιμο πλάτος, μετακινούνται αυτόματα σε ένα μενού
 * «Περισσότερα» — ώστε τίποτα να μη γίνεται ποτέ απρόσιτο, σε καμία συσκευή.
 *
 * @param groups [{ label, icon?, alwaysGroup?, items:[{to,label,icon?,visible}] }]
 * @param dropUp άνοιγμα των dropdowns προς τα ΠΑΝΩ (για το κάτω navbar των tablet)
 */
export default function NavGroups({ groups, dropUp = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(null);       // index ανοιχτού dropdown | 'more'
  const [visibleCount, setVisibleCount] = useState(999);
  const rootRef = useRef(null);
  const measureRef = useRef(null);

  const visible = groups
    .map(g => ({ ...g, items: g.items.filter(i => i.visible) }))
    .filter(g => g.items.length > 0);

  /**
   * Ενεργό στοιχείο, με υποστήριξη query string (π.χ. /reports?g=week).
   * Χωρίς query στο `to`: ενεργό μόνο όταν ούτε το URL έχει σχετικά params.
   */
  const isItemActive = (to) => {
    const [path, qs] = String(to).split('?');
    if (location.pathname !== path) return false;
    const want = new URLSearchParams(qs || '');
    const have = new URLSearchParams(location.search);
    if (![...want.keys()].length) {
      // «Σκέτος» σύνδεσμος: αν κάποιο αδελφό item του ίδιου path χρησιμοποιεί
      // param που υπάρχει στο URL, δεν είναι αυτός ο ενεργός
      const siblingParams = new Set();
      groups.forEach(g => g.items.forEach(i => {
        const [p, q] = String(i.to).split('?');
        if (p === path && q) new URLSearchParams(q).forEach((_, k) => siblingParams.add(k));
      }));
      return ![...siblingParams].some(k => have.has(k));
    }
    return [...want.entries()].every(([k, v]) => have.get(k) === v);
  };

  // Κλείσιμο με κλικ έξω / Escape / αλλαγή σελίδας
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(null); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);
  useEffect(() => { setOpen(null); }, [location.pathname, location.search]);

  /**
   * Μετράει πόσες ομάδες χωρούν πραγματικά. Χρησιμοποιεί κρυφό αντίγραφο για
   * μέτρηση, ώστε να μην «αναπηδά» το ορατό μενού κατά τον υπολογισμό.
   */
  const recalc = useCallback(() => {
    const root = rootRef.current, measure = measureRef.current;
    if (!root || !measure) return;
    const avail = root.clientWidth;
    if (!avail) return;

    const widths = Array.from(measure.children).map(el => el.offsetWidth + 2);
    const MORE_W = 108; // χώρος για το κουμπί «Περισσότερα»
    let used = 0, fit = 0;
    for (let i = 0; i < widths.length; i++) {
      const needsMore = i < widths.length - 1;
      if (used + widths[i] + (needsMore ? MORE_W : 0) <= avail) {
        used += widths[i]; fit++;
      } else break;
    }
    setVisibleCount(Math.max(1, fit));
  }, []);

  useLayoutEffect(() => {
    recalc();
    const ro = new ResizeObserver(recalc);
    if (rootRef.current) ro.observe(rootRef.current);
    window.addEventListener("resize", recalc);
    return () => { ro.disconnect(); window.removeEventListener("resize", recalc); };
  }, [recalc, visible.length]);

  const shown = visible.slice(0, visibleCount);
  const overflow = visible.slice(visibleCount);

  const renderGroup = (g, gi) => {
    const active = g.items.some(i => isItemActive(i.to));

    // Μονό στοιχείο → απλός σύνδεσμος (χωρίς περιττό dropdown)
    if (g.items.length === 1 && !g.alwaysGroup) {
      const it = g.items[0];
      return (
        <Link key={gi} to={it.to} className={`navg-link${active ? " active" : ""}`}>
          {g.icon && <span className="navg-ic">{g.icon}</span>}
          <span className="navg-txt">{tr(it.label)}</span>
        </Link>
      );
    }
    return (
      <div key={gi} className="navg-group">
        <button
          type="button"
          className={`navg-link navg-toggle${active ? " active" : ""}${open === gi ? " open" : ""}`}
          onClick={() => setOpen(open === gi ? null : gi)}
          aria-expanded={open === gi}
        >
          {g.icon && <span className="navg-ic">{g.icon}</span>}
          <span className="navg-txt">{tr(g.label)}</span>
          <span className="navg-caret">▾</span>
        </button>
        {open === gi && (
          <div className="navg-menu">
            {g.items.map((it, ii) => (
              <Link key={ii} to={it.to}
                className={`navg-item${isItemActive(it.to) ? " active" : ""}`}
                onClick={() => setOpen(null)}>
                {it.icon && <span className="navg-ic">{it.icon}</span>}
                {tr(it.label)}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const overflowActive = overflow.some(g => g.items.some(i => isItemActive(i.to)));

  return (
    <div className={`navg-wrap${dropUp ? " navg-dropup" : ""}`}>
      {/* Κρυφό αντίγραφο μόνο για μέτρηση πλάτους */}
      <div className="navg navg-measure" ref={measureRef} aria-hidden="true">
        {visible.map((g, gi) => (
          <span key={gi} className="navg-link">
            {g.icon && <span className="navg-ic">{g.icon}</span>}
            <span className="navg-txt">{tr(g.label)}</span>
            {(g.items.length > 1 || g.alwaysGroup) && <span className="navg-caret">▾</span>}
          </span>
        ))}
      </div>

      <div className="navg" ref={rootRef}>
        {shown.map(renderGroup)}

        {overflow.length > 0 && (
          <div className="navg-group">
            <button
              type="button"
              className={`navg-link navg-toggle${overflowActive ? " active" : ""}${open === "more" ? " open" : ""}`}
              onClick={() => setOpen(open === "more" ? null : "more")}
              aria-expanded={open === "more"}
              title={tr("More")}
            >
              <span className="navg-txt">{tr("More")}</span>
              <span className="navg-caret">▾</span>
            </button>
            {open === "more" && (
              <div className="navg-menu navg-menu-right">
                {overflow.map((g, gi) => (
                  <div key={gi} className="navg-sec">
                    <div className="navg-sec-title">
                      {g.icon && <span className="navg-ic">{g.icon}</span>}{tr(g.label)}
                    </div>
                    {g.items.map((it, ii) => (
                      <Link key={ii} to={it.to}
                        className={`navg-item${isItemActive(it.to) ? " active" : ""}`}
                        onClick={() => setOpen(null)}>
                        {it.icon && <span className="navg-ic">{it.icon}</span>}
                        {tr(it.label)}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
